/**
 * WLSN Scroll Animate
 *
 * Global scroll-triggered fade-in animations. Add class "wlsn-scroll-animate" and
 * data attributes to any HTML element. No custom element required.
 *
 * Usage: <div class="wlsn-scroll-animate" data-animation-desktop="fade-up" data-animation-mobile="fade-up" data-delay="200">...</div>
 *
 * Data attributes:
 * - data-animation: "fade-up" | "fade-down" | "fade-left" | "fade-right" | "none" (both breakpoints)
 * - data-animation-desktop: same values (desktop only)
 * - data-animation-mobile: same values (mobile only)
 * - data-delay: number (ms, e.g. "200")
 * - data-threshold: 0-1 (IntersectionObserver threshold, optional)
 * - data-root-margin: string (e.g. "0px 0px -50px 0px", optional)
 *
 * Global setting (Theme settings → Animations): "Animate once" vs "Every time" (window.theme.wlsnScrollAnimateOnce).
 * Respects prefers-reduced-motion.
 */

class WLSNScrollAnimate {
  constructor() {
    this.classes = {
      animate: 'wlsn-scroll-animate',
      visible: 'wlsn-scroll-animate--visible',
    };
    this.selector = '[data-animation-trigger]';
    this.sliderAnimateSelector = '[data-wlsn-slider-animate]';
    this.sliderSlideSelector = '.swiper-slide';
    this.defaultRootMargin = '0px 0px -50px 0px';
    this.defaultThreshold = 0;
    this.observer = null;
    this.sliderContainerObserver = null;
    this.boundHandleIntersection = this._handleIntersection.bind(this);
  }

  /**
   * Apply animation attributes to slides inside one slider container (called when container enters view)
   * @param {HTMLElement} container - Element with data-wlsn-slider-animate
   * @returns {void}
   */
  _applySliderAnimationsToContainer(container) {
    const animD = container.dataset.wlsnSliderAnimationDesktop || 'fade-up';
    const animM = container.dataset.wlsnSliderAnimationMobile || 'fade-up';
    const stagger = parseInt(container.dataset.wlsnSliderStaggerDelay, 10) || 50;
    const slides = container.querySelectorAll(this.sliderSlideSelector);
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    slides.forEach((slide, i) => {
      slide.setAttribute('data-animation-trigger', '');
      slide.setAttribute('data-animation-desktop', animD);
      slide.setAttribute('data-animation-mobile', animM);
      slide.setAttribute('data-delay', String(i * stagger));
      if (prefersReducedMotion) {
        slide.classList.add(this.classes.visible);
      } else {
        this._applyDelay(slide);
        if (this.observer) {
          this.observer.observe(slide);
        }
      }
    });
  }

  /**
   * Whether to animate only once (true) or every time element enters view (false)
   * @returns {boolean}
   */
  _getAnimateOnce() {
    if (typeof window.theme !== 'undefined' && window.theme.wlsnScrollAnimateOnce !== undefined) {
      return window.theme.wlsnScrollAnimateOnce !== 'every';
    }
    return true;
  }

  /**
   * Apply delay as CSS custom property on element
   * @param {HTMLElement} el - Target element
   * @returns {void}
   */
  _applyDelay(el) {
    const delay = el.dataset.delay != null ? parseInt(el.dataset.delay, 10) : 0;
    if (!isNaN(delay)) {
      el.style.setProperty('--wlsn-animate-delay', `${delay}ms`);
    }
  }

  /**
   * IntersectionObserver callback
   * @param {IntersectionObserverEntry[]} entries
   * @param {IntersectionObserver} observer
   * @returns {void}
   */
  _handleIntersection(entries, observer) {
    const animateOnce = this._getAnimateOnce();
    entries.forEach((entry) => {
      const el = entry.target;
      if (entry.isIntersecting) {
        el.classList.add(this.classes.visible);
        if (animateOnce) {
          observer.unobserve(el);
        }
      } else {
        if (!animateOnce) {
          el.classList.remove(this.classes.visible);
        }
      }
    });
  }

  /**
   * Initialize scroll animation for elements inside rootEl
   * @param {Document|Element} [rootEl=document] - Root to query for .wlsn-scroll-animate
   * @returns {void}
   */
  init(rootEl = document) {
    const elements = rootEl.querySelectorAll(this.selector);
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    if (this.sliderContainerObserver) {
      this.sliderContainerObserver.disconnect();
      this.sliderContainerObserver = null;
    }

    this.observer = new IntersectionObserver(this.boundHandleIntersection, {
      root: null,
      rootMargin: this.defaultRootMargin,
      threshold: this.defaultThreshold,
    });

    elements.forEach((el) => {
      if (prefersReducedMotion) {
        el.classList.add(this.classes.visible);
        return;
      }
      this._applyDelay(el);
      this.observer.observe(el);
    });

    const sliderContainers = rootEl.querySelectorAll(this.sliderAnimateSelector);
    if (sliderContainers.length > 0) {
      sliderContainers.forEach((container) => {
        this._applySliderAnimationsToContainer(container);
      });
    }
  }

  /**
   * Run init on full document (e.g. on DOMContentLoaded or section:reorder)
   * @returns {void}
   */
  run() {
    this.init(document);
  }
}

const wlsnScrollAnimate = new WLSNScrollAnimate();

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => wlsnScrollAnimate.run());
} else {
  wlsnScrollAnimate.run();
}

if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', () => {
    wlsnScrollAnimate.run();
  });
  document.addEventListener('shopify:section:reorder', () => {
    wlsnScrollAnimate.run();
  });
}

window.WLSNScrollAnimate = wlsnScrollAnimate;
